import { useCallback, useMemo, useState } from "react";
import { Link2, Loader2, Plus, Search } from "lucide-react";
import type { PartnerType, PartnerStatus } from "@bookdental/schemas";
import { useSetPageHeader } from "@/lib/page-header";
import { BotaoExportar, buscarTudoPaginado } from "@/components/shared/BotaoExportar";
import { colunasParceiro } from "@/lib/colunas-export";
import { trpc } from "@/lib/trpc";
import { usePodeEditarParceiro } from "@/lib/use-pode-editar-parceiro";
import { cn } from "@/lib/cn";
import { useParametroDaUrl } from "@/lib/use-parametro-da-url";
import { NoAccessBlock } from "@/components/shared/NoAccessBlock";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { CADASTROS_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { buildWhatsAppUrl, WhatsAppIcon } from "@/lib/whatsapp";
import {
  PARTNER_STATUS_META,
  PARTNER_TYPE_META,
  formatDoc,
  formatPhone,
} from "@/components/partners/partner-meta";
import { PartnerDrawer } from "@/components/partners/PartnerDrawer";
import { AcoesDoCadastro } from "@/components/partners/AcoesDoCadastro";
import { ConvidarParceiroDialog } from "@/components/partners/ConvidarParceiroDialog";
import { PartnerInvitesList } from "@/components/partners/PartnerInvitesList";
import { LabConnectionRequestsList } from "@/components/partners/LabConnectionRequestsList";
import { useNavigate } from "react-router";

type TabKey = "all" | PartnerType;

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "all", label: "Todos" },
  { key: "DENTIST", label: "Dentistas" },
  { key: "LAB", label: "Laboratórios" },
  { key: "RADIOLOGY", label: "Radiologias" },
  { key: "CLINIC_NETWORK", label: "Rede" },
  { key: "SUPPLIER", label: "Fornecedores" },
];

export function LabCadastros() {
  const [tab, setTab] = useState<TabKey>("all");
  const [search, setSearch] = useState("");
  // Criar é no diálogo; ver e editar é na página do cadastro (a busca global ainda manda ?abrir=).
  const navigate = useNavigate();
  const [novoTipo, setNovoTipo] = useState<PartnerType | null>(null);
  const abrir = (id: string) => navigate(`/lab/cadastros/${id}`);
  useParametroDaUrl("abrir", abrir);
  const [inviteOpen, setInviteOpen] = useState(false);
  const utils = trpc.useUtils();

  const podeEditar = usePodeEditarParceiro();
  const countsQuery = trpc.partner.countByType.useQuery();

  const listInput = useMemo(
    () => ({
      search: search.trim() || undefined,
      type: tab === "all" ? undefined : tab,
      take: 500,
      skip: 0,
    }),
    [tab, search],
  );

  // Mesma busca e mesma aba que estão na tela — exportar tudo ignoraria o filtro que a pessoa
  // acabou de aplicar.
  const buscarParceirosParaExportar = useCallback(
    () =>
      buscarTudoPaginado<any>(async (skip, take) => {
        const r = await utils.partner.list.fetch({ ...listInput, take, skip });
        return { itens: r.partners as any[], total: r.total };
      }),
    [utils, listInput],
  );

  useTourDaPagina(CADASTROS_TOUR_STEPS);
  useSetPageHeader(
    {
      title: "Cadastros",
      subtitle: "Dentistas, laboratórios, radiologias e fornecedores",
    },
    [],
  );

  const listQuery = trpc.partner.list.useQuery(listInput);
  const partners = listQuery.data?.partners ?? [];

  const meQuery = trpc.me.current.useQuery();
  const canManage = !!meQuery.data?.activeOrganization?.permissions?.["partners.manage"];
  if (meQuery.data && !canManage) {
    return <NoAccessBlock description="Cadastros de parceiros são restritos a quem tem permissão de gerenciá-los." />;
  }

  return (
    <div className="mx-auto w-full max-w-[1600px]">
      {/* Celular: seletor. Seis categorias não cabem numa linha, e as duas saídas anteriores
          tinham defeito próprio — quebrar em três linhas comia o topo da tela, e rolar de lado
          escondia categorias atrás de um gesto. O seletor mostra a escolhida por extenso, com o
          contador, e abre a lista inteira no seletor nativo do sistema. Do `md` pra cima seguem
          as abas de sempre. */}
      <label className="mb-4 block md:hidden">
        <span className="sr-only">Categoria de cadastro</span>
        <select
          value={tab}
          onChange={(e) => setTab(e.target.value as TabKey)}
          className="h-11 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-800 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
        >
          {TABS.map((t) => {
            const count =
              t.key === "all"
                ? Object.values(countsQuery.data ?? {}).reduce((a, b) => a + b, 0)
                : (countsQuery.data?.[t.key] ?? 0);
            return (
              <option key={t.key} value={t.key}>
                {t.label} ({count})
              </option>
            );
          })}
        </select>
      </label>

      <div
        data-tour="cadastros-tabs"
        className="-mx-8 mb-4 hidden items-center gap-1 overflow-x-auto border-b border-zinc-200 px-8 scrollbar-hide md:flex dark:border-border"
      >

        {TABS.map((t) => {
          const count =
            t.key === "all"
              ? Object.values(countsQuery.data ?? {}).reduce((a, b) => a + b, 0)
              : (countsQuery.data?.[t.key] ?? 0);
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                "relative inline-flex h-10 shrink-0 items-center gap-1.5 border-b-2 px-3 text-[13px] font-medium transition-colors",
                active
                  ? "border-brand-600 text-brand-700 dark:border-primary dark:text-primary"
                  : "border-transparent text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
              )}
            >
              <span>{t.label}</span>
              <span
                className={cn(
                  "inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold",
                  active
                    ? "bg-brand-100 text-brand-700 dark:bg-primary/20 dark:text-primary"
                    : "bg-zinc-100 text-zinc-600 dark:bg-muted dark:text-muted-foreground",
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Pedidos de conexão de clínicas + convites pendentes */}
      <LabConnectionRequestsList />
      <PartnerInvitesList />

      {/* Busca + ações */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-full min-w-0 md:max-w-md md:flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..."
            className="h-11 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 md:h-9 md:text-sm dark:border-border dark:bg-card dark:text-foreground"
          />
        </div>
        {listQuery.isFetching && (
          <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
        )}

        <div className="flex w-full flex-wrap items-center gap-1.5 md:ml-auto md:w-auto md:flex-nowrap">
          <BotaoExportar
            nome="cadastros"
            titulo="cadastros"
            planilha="cadastros"
            quantidade={listQuery.data?.total}
            colunas={colunasParceiro((t) => PARTNER_TYPE_META[t as PartnerType]?.label ?? t)}
            idDe={(p) => p.id}
            buscarLinhas={buscarParceirosParaExportar}
            className="h-11 flex-1 justify-center bg-white md:h-9 md:flex-none dark:bg-card"
          />
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 md:h-9 md:flex-none dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-400 dark:hover:bg-emerald-500/20"
          >
            <Link2 className="h-3.5 w-3.5" />
            Convidar parceiro
          </button>
          {/* Criar cadastro é de ADMIN/OWNER — a API recusa, então o botão nem aparece. Gerar
              convite e a lista continuam pra quem tem `partners.manage`. */}
          {podeEditar && (
            <button
              type="button"
              data-tour="cadastros-novo"
              onClick={() => setNovoTipo(tab === "all" ? "DENTIST" : tab)}
              className="inline-flex h-11 flex-[1.6] items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 md:h-9 md:flex-none"
            >
              <Plus className="h-3.5 w-3.5" />
              Novo cadastro
            </button>
          )}
        </div>
      </div>

      {/* Desktop: tabela. Mobile: cards */}
      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white dark:border-border dark:bg-card">
        {listQuery.isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
          </div>
        ) : partners.length === 0 ? (
          <EmptyState
            hasSearch={!!search}
            onNew={() => setNovoTipo(tab === "all" ? "DENTIST" : tab)}
          />
        ) : (
          <>
            {/* Mobile: lista de cards */}
            <ul className="divide-y divide-zinc-100 sm:hidden dark:divide-border/50">
              {partners.map((p: PartnerRow) => (
                <PartnerCardMobile
                  key={p.id}
                  partner={p}
                  showTypeTag={tab === "all"}
                  onClick={() => abrir(p.id)}
                />
              ))}
            </ul>

            {/* Desktop: tabela */}
            <table className="hidden w-full text-sm sm:table">
              <thead className="border-b border-zinc-100 bg-zinc-50/50 text-[11px] uppercase tracking-wider text-zinc-500 dark:border-border dark:bg-background/50 dark:text-muted-foreground">
                <tr>
                  <th className="w-8 px-3 py-2.5" />
                  <th className="px-2 py-2.5 text-left font-semibold">Nome</th>
                  {tab === "all" && (
                    <th className="hidden px-2 py-2.5 text-left font-semibold md:table-cell">
                      Tipo
                    </th>
                  )}
                  <th className="hidden px-2 py-2.5 text-left font-semibold md:table-cell">
                    Contato
                  </th>
                  <th className="hidden px-2 py-2.5 text-left font-semibold lg:table-cell">
                    Documento
                  </th>
                  <th className="hidden px-2 py-2.5 text-left font-semibold lg:table-cell">
                    Cidade
                  </th>
                  <th className="px-2 py-2.5 text-left font-semibold">Status</th>
                  <th className="w-10 px-2 py-2.5" aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {partners.map((p: PartnerRow) => (
                  <PartnerRowItem
                    key={p.id}
                    partner={p}
                    showTypeTag={tab === "all"}
                    onClick={() => abrir(p.id)}
                  />
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <PartnerDrawer tipo={novoTipo} onClose={() => setNovoTipo(null)} onCriado={abrir} />

      <ConvidarParceiroDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
}

type PartnerRow = {
  id: string;
  type: PartnerType;
  name: string;
  tradeName: string | null;
  email: string | null;
  phone: string | null;
  document: string | null;
  croNumber: string | null;
  croState: string | null;
  specialty: string | null;
  city: string | null;
  state: string | null;
  status: PartnerStatus;
  linkedOrganizationId: string | null;
  isPartner: boolean;
  createdAt: Date;
  updatedAt: Date;
};

function PartnerRowItem({
  partner,
  showTypeTag,
  onClick,
}: {
  partner: PartnerRow;
  showTypeTag: boolean;
  onClick: () => void;
}) {
  const meta = PARTNER_TYPE_META[partner.type];
  const statusMeta = PARTNER_STATUS_META[partner.status];
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer border-b border-zinc-100 transition-colors hover:bg-zinc-50 dark:border-border/50 dark:hover:bg-muted/50"
    >
      <td className="px-3 py-2.5">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-md"
          style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}
          title={meta.label}
        >
          <meta.icon className="h-3.5 w-3.5" />
        </span>
      </td>
      <td className="px-2 py-2.5">
        <div className="font-semibold text-zinc-900 dark:text-foreground">
          {partner.name}
        </div>
        {partner.type === "DENTIST" && partner.croNumber && (
          <div className="text-[11px] text-zinc-500 dark:text-muted-foreground">
            CRO-{partner.croState} {partner.croNumber}
            {partner.specialty && ` · ${partner.specialty}`}
          </div>
        )}
        {partner.tradeName && partner.type !== "DENTIST" && (
          <div className="text-[11px] text-zinc-500 dark:text-muted-foreground">
            {partner.tradeName}
          </div>
        )}
      </td>
      {showTypeTag && (
        <td className="hidden px-2 py-2.5 md:table-cell">
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
            style={{
              backgroundColor: `${meta.color}1f`,
              color: meta.color,
              boxShadow: `inset 0 0 0 1px ${meta.color}33`,
            }}
          >
            <meta.icon className="h-3 w-3" />
            {meta.label}
          </span>
        </td>
      )}
      <td className="hidden px-2 py-2.5 md:table-cell">
        {partner.phone && (
          <div className="flex items-center gap-1.5">
            <span className="tabular-nums text-zinc-700 dark:text-foreground/80">
              {formatPhone(partner.phone)}
            </span>
            <a
              href={buildWhatsAppUrl(partner.phone, partner.name)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-emerald-50 hover:text-emerald-600 md:h-6 md:w-6 dark:text-muted-foreground dark:hover:bg-emerald-500/10 dark:hover:text-emerald-400"
              aria-label={`Enviar WhatsApp para ${partner.name}`}
              title="Enviar WhatsApp"
            >
              <WhatsAppIcon className="h-3.5 w-3.5" />
            </a>
          </div>
        )}
        {partner.email && (
          <div className="truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
            {partner.email}
          </div>
        )}
      </td>
      <td className="hidden px-2 py-2.5 tabular-nums text-zinc-700 dark:text-foreground/80 lg:table-cell">
        {formatDoc(partner.document)}
      </td>
      <td className="hidden px-2 py-2.5 text-zinc-700 dark:text-foreground/80 lg:table-cell">
        {[partner.city, partner.state].filter(Boolean).join(" — ")}
      </td>
      <td className="px-2 py-2.5">
        <div className="flex flex-wrap items-center gap-1">
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
            style={{ backgroundColor: statusMeta.bg, color: statusMeta.color }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: statusMeta.color }}
            />
            {statusMeta.label}
          </span>
          {partner.isPartner && (
            <span
              className="inline-flex items-center rounded-full bg-brand-100 px-2 py-0.5 text-[10px] font-bold uppercase text-brand-700 dark:bg-primary/15 dark:text-primary"
              title="Preços personalizados habilitados"
            >
              Parceiro
            </span>
          )}
        </div>
      </td>
      <td className="px-2 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
        <AcoesDoCadastro cadastro={partner} />
      </td>
    </tr>
  );
}

function PartnerCardMobile({
  partner,
  showTypeTag,
  onClick,
}: {
  partner: PartnerRow;
  showTypeTag: boolean;
  onClick: () => void;
}) {
  const meta = PARTNER_TYPE_META[partner.type];
  const statusMeta = PARTNER_STATUS_META[partner.status];
  return (
    <li className="flex items-start">
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 items-start gap-3 py-3 pl-3 text-left transition-colors active:bg-zinc-50 dark:active:bg-muted"
      >
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}
        >
          <meta.icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate font-semibold text-zinc-900 dark:text-foreground">
              {partner.name}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <span
                className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase"
                style={{
                  backgroundColor: statusMeta.bg,
                  color: statusMeta.color,
                }}
              >
                {statusMeta.label}
              </span>
              {partner.isPartner && (
                <span className="inline-flex items-center rounded-full bg-brand-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-brand-700 dark:bg-primary/15 dark:text-primary">
                  Parceiro
                </span>
              )}
            </div>
          </div>
          {showTypeTag && (
            <span
              className="mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold"
              style={{
                backgroundColor: `${meta.color}1f`,
                color: meta.color,
                boxShadow: `inset 0 0 0 1px ${meta.color}33`,
              }}
            >
              <meta.icon className="h-2.5 w-2.5" />
              {meta.label}
            </span>
          )}
          {partner.type === "DENTIST" && partner.croNumber ? (
            <div className="truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
              CRO-{partner.croState} {partner.croNumber}
              {partner.specialty && ` · ${partner.specialty}`}
            </div>
          ) : partner.tradeName ? (
            <div className="truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
              {partner.tradeName}
            </div>
          ) : null}
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-zinc-500 dark:text-muted-foreground">
            {partner.phone && (
              <span className="inline-flex items-center gap-1 tabular-nums">
                {formatPhone(partner.phone)}
                <a
                  href={buildWhatsAppUrl(partner.phone, partner.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-emerald-50 hover:text-emerald-600 md:h-5 md:w-5 dark:text-muted-foreground dark:hover:bg-emerald-500/10 dark:hover:text-emerald-400"
                  aria-label={`Enviar WhatsApp para ${partner.name}`}
                  title="Enviar WhatsApp"
                >
                  <WhatsAppIcon className="h-3 w-3" />
                </a>
              </span>
            )}
            {partner.email && <span className="truncate">{partner.email}</span>}
          </div>
        </div>
      </button>
      <div className="shrink-0 px-1 pt-2.5">
        <AcoesDoCadastro cadastro={partner} />
      </div>
    </li>
  );
}

function EmptyState({
  hasSearch,
  onNew,
}: {
  hasSearch: boolean;
  onNew: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <p className="text-sm font-medium text-zinc-900 dark:text-foreground">
        {hasSearch ? "Nenhum resultado" : "Sem cadastros ainda"}
      </p>
      <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
        {hasSearch
          ? "Tente outro termo de busca."
          : "Adicione seu primeiro cadastro ou envie um convite."}
      </p>
      {!hasSearch && (
        <button
          type="button"
          onClick={onNew}
          className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
        >
          <Plus className="h-4 w-4" />
          Novo cadastro
        </button>
      )}
    </div>
  );
}
